<template>
  <div class="markdown-editor">
    <div v-if="contentOnly" class="content-only">
      <div 
        v-if="localContent"
        ref="previewRef"
        class="prose prose-neutral dark:prose-invert max-w-none preview-content"
        v-html="renderedContent" 
      />
      <div
        v-else
        class="empty-preview"
      >
        Nothing to preview
      </div>
    </div>
    <div 
      v-else
      class="markdown-container"
      :class="{ 'preview-mode': activeTab === 'preview' }"
    >
      <!-- Header with Tabs -->
      <div class="tab-container">
        <button 
          @click="activeTab = 'write'"
          :class="[
            'tab-button',
            activeTab === 'write' 
              ? 'tab-button--active' 
              : 'tab-button--inactive'
          ]"
        >
          Write
        </button>
        <button 
          @click="activeTab = 'preview'"
          :class="[
            'tab-button',
            activeTab === 'preview' 
              ? 'tab-button--active' 
              : 'tab-button--inactive'
          ]"
        >
          Preview
        </button>
        <div class="flex-grow"></div>
        <button 
          v-if="activeTab === 'write'"
          @click="handleImageClick"
          class="image-btn"
          :disabled="disabled"
          title="Add Image (or paste from clipboard)"
        >
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      <!-- Write Tab -->
      <div v-if="activeTab === 'write'" class="content-container">
        <textarea
          v-model="localContent"
          class="markdown-input w-full h-full"
          :placeholder="placeholder"
          @input="handleInput"
          @paste="$emit('paste', $event)"
          :disabled="disabled"
          ref="textareaRef"
        ></textarea>
      </div>

      <!-- Preview Tab -->
      <div v-if="activeTab === 'preview'" class="content-container">
        <div 
          v-if="localContent"
          ref="previewRef"
          class="prose prose-neutral dark:prose-invert max-w-none preview-content"
          v-html="renderedContent" 
        />
        <div
          v-else
          class="empty-preview"
        >
          Nothing to preview
        </div>
      </div>
    </div>
  </div>
</template>
  
<script setup>
import { defineProps, defineEmits, computed, ref, watch, onMounted, nextTick } from 'vue';
import markdownit from "markdown-it";

const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: "Write your content here..."
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  contentOnly: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'paste']);

const activeTab = ref(props.isEditing ? 'preview' : 'write');
const localContent = ref(props.modelValue);

const markdown = markdownit();

watch(() => props.modelValue, (newValue) => {
  if (newValue !== localContent.value) {
    localContent.value = newValue;
  }
});

const textareaRef = ref(null);
const previewRef = ref(null);
const contentHeight = ref(0);

const updateContentHeight = () => {
  if (textareaRef.value) {
 
    textareaRef.value.style.height = '160px';
    textareaRef.value.style.height = 'auto';
    
    const newHeight = !localContent.value ? 160 : Math.max(
      textareaRef.value.scrollHeight,
      160
    );
    
    contentHeight.value = newHeight;
    textareaRef.value.style.height = newHeight + 'px';
  }
};

watch(localContent, () => {
  nextTick(() => {
    updateContentHeight();
  });
}, { immediate: true });

watch(activeTab, () => {
  nextTick(() => {
    updateContentHeight();
  });
});

onMounted(() => {
  nextTick(() => {
    updateContentHeight();
  });
});

const handleInput = () => {
  emit('update:modelValue', localContent.value);
  nextTick(() => {
    updateContentHeight();
  });
};

const renderedContent = computed(() => {
  return markdown.render(localContent.value || '');
});

const handleImageClick = () => {
  const imageInput = document.createElement('input');
  imageInput.type = 'file';
  imageInput.accept = 'image/*';
  imageInput.click();
  
  imageInput.onchange = (event) => {
    const file = event.target.files[0];
    if (file) {
      emit('paste', event);
    }
  };
};
</script>

<style lang="postcss">
.markdown-editor {
  @apply w-full overflow-x-hidden;
}

.markdown-container {
  @apply w-full rounded-2xl overflow-hidden
    bg-echoes-background-black
    border border-echoes-border-gray
    transition-all duration-200 ease-in-out
    hover:bg-echoes-background-black/50
    hover:border-echoes-border-gray;

  &.preview-mode {
    @apply focus-within:ring-0 focus-within:border-echoes-border-gray;
    
    &:hover {
      @apply focus-within:border-echoes-border-gray;
    }
  }
}

.tab-container {
  @apply flex items-center bg-echoes-background-gray;
}

.tab-button {
  @apply px-4 py-2 text-sm transition-colors;
}

.tab-button--active {
  @apply text-white bg-echoes-background-black;
}

.tab-button--inactive {
  @apply text-white/80 bg-echoes-background-gray hover:bg-echoes-background-black/50 hover:text-echoes-primary;
}

.content-container {
  @apply p-3 bg-echoes-background-black overflow-x-hidden transition-all duration-200;
  min-height: v-bind(contentHeight + 'px');
}

.markdown-input {
  @apply w-full text-sm
    bg-transparent
    text-white
    border-none outline-none
    placeholder-white/80
    transition-all duration-200 ease-in-out
    resize-none 
    h-auto
    overflow-hidden;
  min-height: v-bind(contentHeight + 'px');
}

.preview-content {
  min-height: v-bind(contentHeight + 'px');
}

.empty-preview {
  @apply flex items-center justify-center h-[10rem]
    text-white/80 text-sm
    select-none;
}

.content-only {
  @apply w-full overflow-x-hidden p-4;

  .preview-content {
    @apply text-gray-200;
  }
}

.image-btn {
  @apply p-2 mr-4 rounded-[6px] text-white/80 hover:text-echoes-primary hover:bg-echoes-background-black/50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed;
}

.flex-grow {
  @apply flex-1;
}
</style>